import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { doc, getDoc, setDoc, Timestamp } from 'firebase/firestore';
import { db } from '@/lib/firebase';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Checkbox } from '@/components/ui/checkbox';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import Header from '@/components/layout/Header';
import { showSuccess, showError } from '@/lib/swal';
import { ArrowLeft, Building2, Plus, X, Pencil, Check } from 'lucide-react';

export interface UnitTypeRank {
  id: string;
  name: string;
  shortName: string;
  order: number;
}

export interface UnitTypeFieldConfig {
  requireNumber: boolean;
  requireProvince: boolean;
  useNumberForUniqueness: boolean;
}

export interface UnitType {
  id: string;
  name: string;
  entityName: string;
  ceremonies?: string[];
  ranks?: UnitTypeRank[];
  fieldConfig?: UnitTypeFieldConfig;
  createdAt: Date;
  updatedAt: Date;
}

export interface SystemSettings {
  maintenanceMode: boolean;
  currentVersion: string;
  featureFlags: Record<string, boolean>;
  transactionFee: number;
  ranks?: any[];
  unitTypes?: UnitType[];
}


export default function UnitTypesPage() {
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [unitTypes, setUnitTypes] = useState<UnitType[]>([]);
  const [editingId, setEditingId] = useState<string | null>(null);

  useEffect(() => {
    const fetchSettings = async () => {
      try {
        const settingsDoc = await getDoc(doc(db, 'systemSettings', 'platform'));
        
        if (settingsDoc.exists()) {
          const data = settingsDoc.data() as SystemSettings;
          if (data.unitTypes && data.unitTypes.length > 0) {
            setUnitTypes(data.unitTypes.map(ut => ({
              ...ut,
              createdAt: ut.createdAt instanceof Date 
                ? ut.createdAt 
                : (ut.createdAt as any)?.toDate 
                  ? (ut.createdAt as any).toDate() 
                  : new Date(),
              updatedAt: ut.updatedAt instanceof Date 
                ? ut.updatedAt 
                : (ut.updatedAt as any)?.toDate 
                  ? (ut.updatedAt as any).toDate() 
                  : new Date(),
            })));
          } else {
            setUnitTypes([]);
          }
        } else {
          setUnitTypes([]);
        }
      } catch (error) {
        console.error('Error fetching unit types:', error);
        showError('Failed to load unit types');
      } finally {
        setLoading(false);
      }
    };

    fetchSettings();
  }, []);

  const handleSave = async () => {
    // Validate unit types
    const invalidTypes = unitTypes.filter(ut => !ut.name.trim() || !ut.entityName.trim());
    if (invalidTypes.length > 0) {
      showError('All unit types must have both a name and entity name');
      return;
    }

    // Check for duplicate names
    const names = unitTypes.map(ut => ut.name.toLowerCase());
    if (new Set(names).size !== names.length) {
      showError('Unit type names must be unique');
      return;
    }

    setSaving(true);
    try {
      const settingsDoc = await getDoc(doc(db, 'systemSettings', 'platform'));
      const currentSettings = settingsDoc.exists() 
        ? settingsDoc.data() as SystemSettings
        : {
            maintenanceMode: false,
            currentVersion: '1.0.0',
            featureFlags: {},
            transactionFee: 1.00,
            ranks: [],
          };

      const updatedSettings: SystemSettings = {
        ...currentSettings,
        unitTypes: unitTypes.map(ut => ({
          ...ut,
          createdAt: ut.createdAt ? Timestamp.fromDate(ut.createdAt) : Timestamp.now(),
          updatedAt: Timestamp.now(),
        })) as any,
      };

      await setDoc(doc(db, 'systemSettings', 'platform'), updatedSettings);
      showSuccess('Unit types updated successfully!');
      setEditingId(null);
    } catch (error: any) {
      console.error('Error saving unit types:', error);
      
      // Provide more specific error messages
      if (error?.code === 'permission-denied') {
        showError('Permission denied: You must be an admin to save unit types. Please ensure your user account has admin privileges.');
      } else if (error?.code === 'unauthenticated') {
        showError('Not authenticated: Please log in and try again.');
      } else {
        showError(`Failed to save unit types: ${error?.message || 'Unknown error'}`);
      }
    } finally {
      setSaving(false);
    }
  };

  const handleAddUnitType = () => {
    const now = new Date();
    const newUnitType: UnitType = {
      id: crypto.randomUUID(),
      name: '',
      entityName: '',
      fieldConfig: {
        requireNumber: true,
        requireProvince: false,
        useNumberForUniqueness: true,
      },
      createdAt: now,
      updatedAt: now,
    };
    setUnitTypes([...unitTypes, newUnitType]);
    setEditingId(newUnitType.id);
  };

  const handleRemoveUnitType = (id: string) => {
    if (unitTypes.length <= 1) {
      showError('Must have at least one unit type');
      return;
    }
    setUnitTypes(unitTypes.filter(ut => ut.id !== id));
  };

  const handleUpdateUnitType = (id: string, field: keyof UnitType, value: string | string[]) => {
    setUnitTypes(unitTypes.map(ut => 
      ut.id === id ? { ...ut, [field]: value } : ut
    ));
  };

  const handleUpdateFieldConfig = (id: string, field: keyof UnitTypeFieldConfig, value: boolean) => {
    setUnitTypes(unitTypes.map(ut => 
      ut.id === id 
        ? { 
            ...ut, 
            fieldConfig: {
              ...ut.fieldConfig,
              requireNumber: ut.fieldConfig?.requireNumber ?? true,
              requireProvince: ut.fieldConfig?.requireProvince ?? false,
              useNumberForUniqueness: ut.fieldConfig?.useNumberForUniqueness ?? true,
              [field]: value,
            }
          } 
        : ut
    ));
  };

  const handleAddRank = (unitTypeId: string) => {
    setUnitTypes(unitTypes.map(ut => {
      if (ut.id === unitTypeId) {
        const currentRanks = ut.ranks || [];
        const newRank: UnitTypeRank = {
          id: crypto.randomUUID(),
          name: '',
          shortName: '',
          order: currentRanks.length + 1,
        };
        return { ...ut, ranks: [...currentRanks, newRank] };
      }
      return ut;
    }));
  };

  const handleRemoveRank = (unitTypeId: string, rankId: string) => {
    setUnitTypes(unitTypes.map(ut => {
      if (ut.id === unitTypeId) {
        const filteredRanks = (ut.ranks || []).filter(r => r.id !== rankId);
        return { ...ut, ranks: filteredRanks };
      }
      return ut;
    }));
  };

  const handleUpdateRank = (unitTypeId: string, rankId: string, field: keyof UnitTypeRank, value: string | number) => {
    setUnitTypes(unitTypes.map(ut => {
      if (ut.id === unitTypeId) {
        return {
          ...ut,
          ranks: (ut.ranks || []).map(r => 
            r.id === rankId ? { ...r, [field]: value } : r
          )
        };
      }
      return ut;
    }));
  };

  const handleMoveRank = (unitTypeId: string, rankId: string, direction: 'up' | 'down') => {
    setUnitTypes(unitTypes.map(ut => {
      if (ut.id === unitTypeId) {
        const ranks = ut.ranks || [];
        const index = ranks.findIndex(r => r.id === rankId);
        if (index === -1) return ut;
        
        const targetIndex = direction === 'up' ? index - 1 : index + 1;
        if (targetIndex < 0 || targetIndex >= ranks.length) return ut;
        
        const newRanks = [...ranks];
        [newRanks[index], newRanks[targetIndex]] = [newRanks[targetIndex], newRanks[index]];
        
        return { ...ut, ranks: newRanks };
      }
      return ut;
    }));
  };


  if (loading) {
    return (
      <div className="flex min-h-screen flex-col bg-background">
        <Header />
        <div className="flex flex-1 items-center justify-center">
          <div className="text-center">
            <div className="mb-4 h-12 w-12 animate-spin rounded-full border-4 border-primary border-t-transparent mx-auto" />
            <p className="text-muted-foreground">Loading unit types...</p>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="flex min-h-screen flex-col bg-background">
      <Header />
      <main className="flex-1 px-4 py-8">
        <div className="container mx-auto max-w-4xl">
          <div className="mb-8">
            <Button variant="ghost" onClick={() => navigate('/dashboard')} className="mb-4">
              <ArrowLeft className="mr-2 h-4 w-4" />
              Back to Dashboard
            </Button>
            <h1 className="text-3xl font-bold text-foreground">Manage Unit Types</h1>
            <p className="text-muted-foreground">Configure the types of masonic units supported by the platform</p>
          </div>

          <div className="space-y-6">
            <Card>
              <CardHeader>
                <CardTitle className="flex items-center">
                  <Building2 className="mr-2 h-5 w-5" />
                  Unit Types
                </CardTitle>
                <CardDescription>
                  Define the different types of masonic units (e.g., Craft Unit, Royal Arch Chapter) that can be created on the platform.
                </CardDescription>
              </CardHeader>
              <CardContent>
                <div className="space-y-4">
                  {unitTypes.length === 0 ? (
                    <div className="text-center py-8 text-muted-foreground border-2 border-dashed rounded-lg">
                      <Building2 className="h-12 w-12 mx-auto mb-4 opacity-50" />
                      <p className="text-sm font-medium">No unit types configured</p>
                      <p className="text-xs mt-1">Click "Add Unit Type" to create your first unit type</p>
                    </div>
                  ) : (
                    <div className="space-y-3">
                      {unitTypes.map((unitType) => (
                      <div key={unitType.id} className="flex items-start gap-2 p-3 rounded-lg border">
                        {editingId === unitType.id ? (
                          <>
                            <div className="flex-1 space-y-3">
                              <div className="grid grid-cols-2 gap-2">
                                <div className="space-y-1">
                                  <Label className="text-xs">Type Name</Label>
                                  <Input
                                    value={unitType.name}
                                    onChange={(e) => handleUpdateUnitType(unitType.id, 'name', e.target.value)}
                                    placeholder="e.g., Craft"
                                  />
                                </div>
                                <div className="space-y-1">
                                  <Label className="text-xs">Entity Name</Label>
                                  <Input
                                    value={unitType.entityName}
                                    onChange={(e) => handleUpdateUnitType(unitType.id, 'entityName', e.target.value)}
                                    placeholder="e.g., Unit"
                                  />
                                </div>
                              </div>
                              <div className="space-y-1">
                                <Label className="text-xs">Ceremonies</Label>
                                <div className="space-y-2">
                                  {(unitType.ceremonies || []).map((ceremony, ceremonyIndex) => (
                                    <div key={ceremonyIndex} className="flex gap-2">
                                      <Input
                                        value={ceremony}
                                        onChange={(e) => {
                                          const newCeremonies = [...(unitType.ceremonies || [])];
                                          newCeremonies[ceremonyIndex] = e.target.value;
                                          handleUpdateUnitType(unitType.id, 'ceremonies', newCeremonies);
                                        }}
                                        placeholder="e.g., Installation"
                                      />
                                      <Button
                                        type="button"
                                        variant="ghost"
                                        size="sm"
                                        onClick={() => {
                                          const newCeremonies = (unitType.ceremonies || []).filter((_, i) => i !== ceremonyIndex);
                                          handleUpdateUnitType(unitType.id, 'ceremonies', newCeremonies);
                                        }}
                                      >
                                        <X className="h-4 w-4" />
                                      </Button>
                                    </div>
                                  ))}
                                  <Button
                                    type="button"
                                    variant="outline"
                                    size="sm"
                                    onClick={() => {
                                      const newCeremonies = [...(unitType.ceremonies || []), ''];
                                      handleUpdateUnitType(unitType.id, 'ceremonies', newCeremonies);
                                    }}
                                  >
                                    <Plus className="mr-2 h-4 w-4" />
                                    Add Ceremony
                                  </Button>
                                </div>
                              </div>
                              <div className="space-y-1">
                                <Label className="text-xs">Field Configuration</Label>
                                <div className="space-y-2 p-3 border rounded-lg bg-muted/30">
                                  <div className="flex items-center space-x-2">
                                    <Checkbox
                                      id={`requireNumber-${unitType.id}`}
                                      checked={unitType.fieldConfig?.requireNumber ?? true}
                                      onCheckedChange={(checked: boolean) => 
                                        handleUpdateFieldConfig(unitType.id, 'requireNumber', checked === true)
                                      }
                                    />
                                    <label
                                      htmlFor={`requireNumber-${unitType.id}`}
                                      className="text-sm font-medium leading-none peer-disabled:cursor-not-allowed peer-disabled:opacity-70 cursor-pointer"
                                    >
                                      Require Unit Number
                                    </label>
                                  </div>
                                  <div className="flex items-center space-x-2">
                                    <Checkbox
                                      id={`useNumberForUniqueness-${unitType.id}`}
                                      checked={unitType.fieldConfig?.useNumberForUniqueness ?? true}
                                      onCheckedChange={(checked: boolean) => 
                                        handleUpdateFieldConfig(unitType.id, 'useNumberForUniqueness', checked === true)
                                      }
                                    />
                                    <label
                                      htmlFor={`useNumberForUniqueness-${unitType.id}`}
                                      className="text-sm font-medium leading-none peer-disabled:cursor-not-allowed peer-disabled:opacity-70 cursor-pointer"
                                    >
                                      Use Number for Uniqueness Check
                                    </label>
                                  </div>
                                  <div className="flex items-center space-x-2">
                                    <Checkbox
                                      id={`requireProvince-${unitType.id}`}
                                      checked={unitType.fieldConfig?.requireProvince ?? false}
                                      onCheckedChange={(checked: boolean) => 
                                        handleUpdateFieldConfig(unitType.id, 'requireProvince', checked === true)
                                      }
                                    />
                                    <label
                                      htmlFor={`requireProvince-${unitType.id}`}
                                      className="text-sm font-medium leading-none peer-disabled:cursor-not-allowed peer-disabled:opacity-70 cursor-pointer"
                                    >
                                      Require Province
                                    </label>
                                  </div>
                                  <p className="text-xs text-muted-foreground mt-2">
                                    Configure which fields are required when creating units of this type.
                                  </p>
                                </div>
                              </div>
                              <div className="space-y-1">
                                <Label className="text-xs">Ranks/Prefixes</Label>
                                <div className="space-y-2">
                                  {(unitType.ranks || []).map((rank, rankIndex) => (
                                    <div key={rank.id} className="flex items-center gap-2">
                                      <div className="flex flex-col gap-1">
                                        <Button
                                          type="button"
                                          variant="ghost"
                                          size="sm"
                                          className="h-5 w-5 p-0"
                                          onClick={() => handleMoveRank(unitType.id, rank.id, 'up')}
                                          disabled={rankIndex === 0}
                                        >
                                          ▲
                                        </Button>
                                        <Button
                                          type="button"
                                          variant="ghost"
                                          size="sm"
                                          className="h-5 w-5 p-0"
                                          onClick={() => handleMoveRank(unitType.id, rank.id, 'down')}
                                          disabled={rankIndex === (unitType.ranks?.length || 0) - 1}
                                        >
                                          ▼
                                        </Button>
                                      </div>
                                      <div className="flex-1 grid grid-cols-2 gap-2">
                                        <Input
                                          value={rank.name}
                                          onChange={(e) => handleUpdateRank(unitType.id, rank.id, 'name', e.target.value)}
                                          placeholder="Full name (e.g., Worshipful Brother)"
                                          className="text-sm"
                                        />
                                        <Input
                                          value={rank.shortName}
                                          onChange={(e) => handleUpdateRank(unitType.id, rank.id, 'shortName', e.target.value)}
                                          placeholder="Short (e.g., WBro)"
                                          className="text-sm"
                                        />
                                      </div>
                                      <Button
                                        type="button"
                                        variant="ghost"
                                        size="sm"
                                        onClick={() => handleRemoveRank(unitType.id, rank.id)}
                                      >
                                        <X className="h-4 w-4" />
                                      </Button>
                                    </div>
                                  ))}
                                  <Button
                                    type="button"
                                    variant="outline"
                                    size="sm"
                                    onClick={() => handleAddRank(unitType.id)}
                                  >
                                    <Plus className="mr-2 h-4 w-4" />
                                    Add Rank
                                  </Button>
                                </div>
                              </div>
                            </div>
                            <Button
                              type="button"
                              variant="ghost"
                              size="sm"
                              onClick={() => setEditingId(null)}
                            >
                              <Check className="h-4 w-4" />
                            </Button>
                          </>
                        ) : (
                          <>
                            <div className="flex-1">
                              <div className="flex items-center justify-between mb-1">
                                <div>
                                  <p className="font-medium">{unitType.name}</p>
                                  <p className="text-sm text-muted-foreground">Entity: {unitType.entityName}</p>
                                </div>
                              </div>
                              {unitType.ceremonies && unitType.ceremonies.length > 0 && (
                                <div className="mt-2">
                                  <p className="text-xs text-muted-foreground mb-1">Ceremonies:</p>
                                  <div className="flex flex-wrap gap-1">
                                    {unitType.ceremonies.map((ceremony, idx) => (
                                      <span key={idx} className="inline-flex items-center px-2 py-0.5 rounded text-xs bg-primary/10 text-primary">
                                        {ceremony}
                                      </span>
                                    ))}
                                  </div>
                                </div>
                              )}
                              {unitType.fieldConfig && (
                                <div className="mt-2">
                                  <p className="text-xs text-muted-foreground mb-1">Field Configuration:</p>
                                  <div className="flex flex-wrap gap-1 text-xs">
                                    {unitType.fieldConfig.requireNumber && (
                                      <span className="inline-flex items-center px-2 py-0.5 rounded bg-blue-100 text-blue-700 dark:bg-blue-900/30 dark:text-blue-400">
                                        Number Required
                                      </span>
                                    )}
                                    {!unitType.fieldConfig.requireNumber && (
                                      <span className="inline-flex items-center px-2 py-0.5 rounded bg-gray-100 text-gray-700 dark:bg-gray-900/30 dark:text-gray-400">
                                        Number Optional
                                      </span>
                                    )}
                                    {unitType.fieldConfig.useNumberForUniqueness && (
                                      <span className="inline-flex items-center px-2 py-0.5 rounded bg-purple-100 text-purple-700 dark:bg-purple-900/30 dark:text-purple-400">
                                        Number for Uniqueness
                                      </span>
                                    )}
                                    {unitType.fieldConfig.requireProvince && (
                                      <span className="inline-flex items-center px-2 py-0.5 rounded bg-green-100 text-green-700 dark:bg-green-900/30 dark:text-green-400">
                                        Province Required
                                      </span>
                                    )}
                                  </div>
                                </div>
                              )}
                              {unitType.ranks && unitType.ranks.length > 0 && (
                                <div className="mt-2">
                                  <p className="text-xs text-muted-foreground mb-1">Ranks/Prefixes ({unitType.ranks.length}):</p>
                                  <div className="text-xs space-y-1">
                                    {unitType.ranks.map((rank, idx) => (
                                      <div key={rank.id} className="flex items-center gap-2">
                                        <span className="text-muted-foreground w-4">{idx + 1}.</span>
                                        <span className="font-medium">{rank.name}</span>
                                        <span className="text-muted-foreground">({rank.shortName})</span>
                                      </div>
                                    ))}
                                  </div>
                                </div>
                              )}
                            </div>
                            <Button
                              type="button"
                              variant="ghost"
                              size="sm"
                              onClick={() => setEditingId(unitType.id)}
                            >
                              <Pencil className="h-4 w-4" />
                            </Button>
                          </>
                        )}
                        
                        <Button
                          type="button"
                          variant="ghost"
                          size="sm"
                          onClick={() => handleRemoveUnitType(unitType.id)}
                          disabled={unitTypes.length === 1}
                        >
                          <X className="h-4 w-4" />
                        </Button>
                      </div>
                      ))}
                    </div>
                  )}

                  <div className="flex gap-2">
                    <Button
                      type="button"
                      variant="outline"
                      onClick={handleAddUnitType}
                    >
                      <Plus className="mr-2 h-4 w-4" />
                      Add Unit Type
                    </Button>
                  </div>

                  <div className="flex gap-2 pt-4 border-t">
                    <Button onClick={handleSave} disabled={saving}>
                      {saving ? 'Saving...' : 'Save Changes'}
                    </Button>
                  </div>
                </div>
              </CardContent>
            </Card>

            <Card>
              <CardHeader>
                <CardTitle>How Unit Types Work</CardTitle>
              </CardHeader>
              <CardContent className="space-y-4">
                <div>
                  <h4 className="font-medium mb-2">Type Name</h4>
                  <p className="text-sm text-muted-foreground">
                    The general category of the masonic unit (e.g., "Craft", "Holy Royal Arch", "Mark").
                  </p>
                </div>
                <div>
                  <h4 className="font-medium mb-2">Entity Name</h4>
                  <p className="text-sm text-muted-foreground">
                    The specific term used for units of this type (e.g., "Unit" for Craft, "Chapter" for Royal Arch).
                    This will be used throughout the platform in labels like "Chapter Management" or "Create New Unit".
                  </p>
                </div>
                <div>
                  <h4 className="font-medium mb-2">Ceremonies</h4>
                  <p className="text-sm text-muted-foreground">
                    Define the types of ceremonies that can be held for this unit type (e.g., "Installation", "Initiation", "Exaltation").
                    These will be available as options when creating events for units of this type.
                  </p>
                </div>
                <div>
                  <h4 className="font-medium mb-2">Ranks/Prefixes</h4>
                  <p className="text-sm text-muted-foreground">
                    Define the ranks or prefixes available for members of this unit type (e.g., "Brother/Bro", "Worshipful Brother/WBro").
                    Each rank has a full name (used in formal contexts) and a short name (used in abbreviated contexts like place cards).
                    Users can select their rank when setting up their primary unit affiliation, and these will be used in RSVPs and place cards.
                    The order of ranks can be adjusted using the up/down arrows.
                  </p>
                </div>
                <div>
                  <h4 className="font-medium mb-2">Field Configuration</h4>
                  <p className="text-sm text-muted-foreground mb-2">
                    Configure which fields are required and how uniqueness is determined for this unit type:
                  </p>
                  <ul className="text-sm text-muted-foreground space-y-1 list-disc list-inside pl-2">
                    <li><strong>Require Unit Number:</strong> When enabled, users must provide a number when creating units of this type. Disable this for unit types like "Club" that don't use numbers.</li>
                    <li><strong>Use Number for Uniqueness Check:</strong> When enabled, the system checks if a unit with the same name and number already exists. Disable this if numbers aren't required or aren't meaningful for uniqueness.</li>
                    <li><strong>Require Province:</strong> When enabled, users must specify a province when creating units of this type.</li>
                  </ul>
                </div>
                <div>
                  <h4 className="font-medium mb-2">Uniqueness</h4>
                  <p className="text-sm text-muted-foreground">
                    Units with the same name and number are allowed across different types, but there can only be one unique unit per type.
                    For example, "St. John's No. 1" can exist as both a Craft Unit and a Royal Arch Chapter. Uniqueness checking respects the field configuration for each unit type.
                  </p>
                </div>
              </CardContent>
            </Card>
          </div>
        </div>
      </main>
    </div>
  );
}

